import React, {ReactNode, useMemo} from 'react';

import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {useHistory, useRouteMatch} from 'react-router-dom';

import {PillItem} from 'components/ui-kit/Pills';
import {PERMISSION_ALIASES} from 'core/constants/permissionAliases';
import {ROUTE} from 'core/routes';
import {isCustomsRole$, isNkRole$, permissions$, profileState$} from 'models/auth';

import {UserPermission, UserPermissionValue} from 'types';

import {CustomPills, CustomTitle, NavigationContainer, Heading} from './styled';

type Props = {
    headingContent?: ReactNode;
};

type NavigationItem = PillItem & {route: string; permissions?: UserPermissionValue[]};

const isBranchesNkTabVisible = false;

export const ProfileNavigationTabs = ({headingContent}: Props) => {
    const {t} = useTranslation();
    const history = useHistory();
    const {path} = useRouteMatch();
    const state = useStore(profileState$);
    const isCustomsRole = useStore(isCustomsRole$);
    const isNkRole = useStore(isNkRole$);
    const permissions = useStore(permissions$);
    const canViewPersonnelNk = permissions.includes(PERMISSION_ALIASES.personnelNk);
    const canManageBranchesNk = permissions.includes(PERMISSION_ALIASES.nkBranchManage);
    const canViewOrganizations = permissions.includes(
        PERMISSION_ALIASES.registeredTurnoverParticipantsView,
    );

    const items: NavigationItem[] = useMemo(() => {
        const commonDataItem: NavigationItem = {
            label: t('pages.profile.pills.CommonData'),
            cb: () => history.push(ROUTE.PROFILE),
            route: ROUTE.PROFILE,
        };
        const factoriesItem: NavigationItem = {
            label: t('pages.profile.pills.Factories'),
            cb: () => history.push(ROUTE.FACTORIES),
            route: ROUTE.FACTORIES,
            permissions: [UserPermission.FACTORY_GRID_VIEW],
        };
        const personnelNkItem: NavigationItem = {
            label: t('pages.profile.pills.PersonnelNk'),
            cb: () => history.push(ROUTE.PERSONNEL_NK),
            route: ROUTE.PERSONNEL_NK,
        };
        const branchesNkItem: NavigationItem = {
            label: t('pages.profile.pills.BranchesNk'),
            cb: () => history.push(ROUTE.BRANCHES_NK),
            route: ROUTE.BRANCHES_NK,
        };
        const staffItem: NavigationItem = {
            label: t('pages.profile.pills.Staff'),
            cb: () => history.push(ROUTE.STAFF),
            route: ROUTE.STAFF,
        };
        const customsStaffItem: NavigationItem = {
            label: t('pages.profile.pills.Staff'),
            cb: () => history.push(ROUTE.CUSTOMS_USERS_LIST),
            route: ROUTE.CUSTOMS_USERS_LIST,
        };
        const storagesItem: NavigationItem = {
            label: t('pages.profile.pills.Storages'),
            cb: () => history.push(ROUTE.STORAGES),
            route: ROUTE.STORAGES,
            permissions: [
                PERMISSION_ALIASES.allStoragesView,
                PERMISSION_ALIASES.userOwnStoragesView,
            ],
        };
        const billingItem: NavigationItem = {
            label: t('pages.profile.pills.Billing'),
            cb: () => history.push(ROUTE.BILLING),
            route: ROUTE.BILLING,
            permissions: [
                PERMISSION_ALIASES.transactionsView,
                PERMISSION_ALIASES.transactionsAllView,
                PERMISSION_ALIASES.paymentInvoicesView,
            ],
        };
        const customsItem: NavigationItem = {
            label: t('pages.profile.pills.Customs'),
            cb: () => history.push(ROUTE.CUSTOMS),
            route: ROUTE.CUSTOMS,
            permissions: [UserPermission.CUSTOMS_LIST],
        };
        const organizationsItem: NavigationItem = {
            label: t('pages.profile.pills.Organizations'),
            cb: () => history.push(ROUTE.ORGANIZATIONS),
            route: ROUTE.ORGANIZATIONS,
        };

        if (isNkRole) {
            const nkItems = [commonDataItem];

            if (canViewPersonnelNk) nkItems.push(personnelNkItem);
            if (isBranchesNkTabVisible && canManageBranchesNk) nkItems.push(branchesNkItem);

            nkItems.push(storagesItem, billingItem, customsItem);

            return nkItems;
        }

        const roleItems = isCustomsRole
            ? [
                  commonDataItem,
                  factoriesItem,
                  customsStaffItem,
                  storagesItem,
                  billingItem,
                  customsItem,
              ]
            : [commonDataItem, factoriesItem, staffItem, storagesItem, billingItem, customsItem];

        if (canViewOrganizations) roleItems.push(organizationsItem);

        return roleItems;
    }, [t, isCustomsRole, isNkRole, canViewPersonnelNk, canManageBranchesNk, canViewOrganizations]);

    const pathIndex = () => {
        if (path === ROUTE.BILLING_INVOICES) {
            return items.findIndex((item) => item.route === ROUTE.BILLING);
        }

        return items.findIndex((item) => item.route === path);
    };

    return (
        <NavigationContainer>
            <Heading>
                <CustomTitle size={4}>{state.profile?.legalPerson?.company_name || ''}</CustomTitle>
                {headingContent}
            </Heading>
            <CustomPills forceIndex={pathIndex()} items={items} />
        </NavigationContainer>
    );
};
